import { readFragment, ResultOf } from "gql.tada";
import { NewsReelSectionById } from "../../queries/entries/NewsReelSectionById";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import IconArrowUpRight from "@hawkins/assets/lib/icons/iconArrowUpRight";
import { Link } from "../shared/core/Link";
import { Button } from "../shared/core/Button";
import { LinkFragment } from "../../queries/fragments/LinkFragment";

export const NewsReelSection = ({
  entry: { newsReelSection: entry },
}: {
  entry: ResultOf<typeof NewsReelSectionById>;
}) => {
  const isDesktop = useIsDesktop();

  if (!entry) return null;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center container-base">
      <p className="text-body-small text-center mb-15 mt-20 text-brand-cream">
        {entry.headline}
      </p>
      <h1 className="text-heading-3 text-center mb-15 mt-20 text-brand-cream">
        {entry.title}
      </h1>
      <div className="flex flex-col dsk:flex-row gap-1 mt-20 mb-20 group/news-items">
        {entry.newsRealItemCollection?.items
          ?.slice(0, isDesktop ? 6 : 3)
          .map((item, index) => (
            <NewsReelCard
              key={index}
              item={item}
              readSegment={entry.readMore ?? ""}
              isDesktop={isDesktop}
            />
          ))}
      </div>
      <p className="max-dsk:hidden text-center mt-18 mb-4 w-[402px] break-words">
        {entry.description}
      </p>
      {!!entry.viewAllUrl && (
        <Button asChild variant="light-on-blur" size="large" className="mb-20">
          <Link href={entry.viewAllUrl ?? ""}>{entry.viewAll}</Link>
        </Button>
      )}
    </div>
  );
};

const NewsReelCard = ({
  item,
  readSegment,
  isDesktop,
}: {
  item: Exclude<
    NonNullable<
      ResultOf<typeof NewsReelSectionById>["newsReelSection"]
    >["newsRealItemCollection"],
    null
  >["items"][number];
  readSegment: string;
  isDesktop: boolean;
}) => {
  const link = readFragment(LinkFragment, item?.link);

  if (!link) return null;

  return (
    <Link link={link} className="block group">
      <div className="relative flex flex-col items-center p-4 rounded-lg transition-opacity dsk:group-hover/news-items:opacity-30 dsk:group-hover/news-items:group-hover:opacity-100">
        <div className="relative w-full aspect-[5/4] h-60 overflow-hidden rounded-lg">
          <img
            className="h-full w-full object-cover transition-all group-hover:scale-110 dsk:group-[&:not(:hover)]:group-hover/news-items:dsk:opacity-30 group-hover/news-items:opacity-30 group-hover/news-items:group-hover:opacity-40"
            src={item?.image?.url ?? ""}
            alt=""
          />
          {isDesktop && (
            <div className="absolute left-0 right-0 top-1/2 transform flex items-center justify-center bg-opacity-50 text-white rounded-lg transition-all opacity-0 group-hover:opacity-100 translate-y-[calc(-50%+20px)] group-hover:-translate-y-1/2">
              <span className="flex items-center">
                {readSegment}
                <IconArrowUpRight className="ml-2" />
              </span>
            </div>
          )}
        </div>
        <div className="text-brand-cream text-body-small text-center mt-2 p-2 w-full max-w-[240px] break-words transition-transform group-hover:translate-y-3">
          {link?.text}
        </div>
        {!isDesktop && (
          <div className="mt-2 flex items-center justify-center bg-opacity-50 text-white rounded-lg px-4 py-2 max-w-[240px]">
            <span className="flex items-center">
              {readSegment}
              <IconArrowUpRight className="ml-2" />
            </span>
          </div>
        )}
      </div>
    </Link>
  );
};
